import React from "react";

import { PopoverSelect } from "@/components/molecules/PopoverSelect/PopoverSelect";
import { type IWidgetFilterItem, useWidgetFiltersController } from "./useWidgetFiltersController";
import { Calendar } from "lucide-react";

export interface IPeriodFilterProps {
    controller: ReturnType<typeof useWidgetFiltersController>;
    widget: any;
    showPopoverContent?: boolean;
}

export const PERIOD_OPTIONS: IWidgetFilterItem<string>[] = [
    { id: 'dashboard_date', text: 'Dashboard Date', data: 'dashboard_date' },
    { id: 'yesterday', text: 'Yesterday', data: 'yesterday' },
    { id: 'last_7_days', text: 'Last 7 Days', data: 'last_7_days' },
    { id: 'last_14_days', text: 'Last 14 Days', data: 'last_14_days' },
    { id: 'last_30_days', text: 'Last 30 Days', data: 'last_30_days' },
    { id: 'this_month', text: 'This Month', data: 'this_month' },
    { id: 'last_month', text: 'Last Month', data: 'last_month' },
    { id: 'this_year', text: 'This Year', data: 'this_year' },
    { id: 'last_year', text: 'Last Year', data: 'last_year' },
];

// Function to get period options including widget's current datePeriod if not in standard options
const getPeriodOptions = (widgetDatePeriod?: string): IWidgetFilterItem<string>[] => {
    if (!widgetDatePeriod) return PERIOD_OPTIONS;
    
    // Check if widget's datePeriod is already in standard options
    const isStandardOption = PERIOD_OPTIONS.some(option => option.id === widgetDatePeriod);
    
    if (isStandardOption) {
        return PERIOD_OPTIONS;
    }
    
    // Add widget's datePeriod as a custom option
    const customOption: IWidgetFilterItem<string> = {
        id: widgetDatePeriod,
        text: widgetDatePeriod.charAt(0).toUpperCase() + widgetDatePeriod.slice(1).replace(/_/g, ' '),
        data: widgetDatePeriod
    };
    
    return [...PERIOD_OPTIONS, customOption];
};

export const PeriodFilter: React.FC<IPeriodFilterProps> = ({ 
    controller, 
    widget,
    showPopoverContent = true
}) => {
    const { selectedPeriodItems, setSelectedPeriodItems } = controller;
    
    // Get dynamic period options including widget's current datePeriod if needed
    const periodOptions = getPeriodOptions(widget?.datePeriod);

    // Initialize with widget's datePeriod if no selection exists
    React.useEffect(() => {
        if (selectedPeriodItems.length === 0 && widget?.datePeriod) {
            const defaultOption = periodOptions.find(option => option.id === widget.datePeriod);
            if (defaultOption) {
                setSelectedPeriodItems([defaultOption], true);
            }
        }
    }, [widget?.datePeriod, selectedPeriodItems.length, setSelectedPeriodItems, periodOptions]);

    const handleSelectionChange = (selectedItem: IWidgetFilterItem<string>) => {
        // Update the controller state - this will trigger the useEffect in WidgetFactory
        setSelectedPeriodItems([selectedItem]);
    };

    // Use current selection or fall back to widget's datePeriod
    const currentSelectedItem = selectedPeriodItems.length > 0 
        ? selectedPeriodItems[0]
        : (() => {
            const defaultPeriod = widget?.datePeriod || 'last_30_days';
            return periodOptions.find(option => option.id === defaultPeriod) || periodOptions[0];
        })();

    if (!widget?.showPeriodSelector) {
        return null;
    }

    return (
        <PopoverSelect
            showPopoverContent={showPopoverContent}
            items={periodOptions}
            selectedItem={currentSelectedItem}
            onSelectionChange={handleSelectionChange}
            triggerLabel="Period:"
            width="w-48"
            disableSearch={true}
            prefixIcon={<Calendar className="h-3.5 w-3.5" />}
        />
    );
};
